import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * Modal de Confirmação Personalizado
 * 
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {string} props.title - Título do modal
 * @param {string} props.message - Mensagem principal
 * @param {string} props.type - Tipo de mensagem: 'warning' | 'info' | 'success' | 'error'
 * @param {function} props.onConfirm - Função executada ao confirmar
 * @param {function} props.onCancel - Função executada ao cancelar
 * @param {string} props.confirmText - Texto do botão confirmar (padrão: "Confirmar")
 * @param {string} props.cancelText - Texto do botão cancelar (padrão: "Cancelar")
 * @param {boolean} props.loading - Se está carregando
 * @param {string} props.description - Descrição adicional (opcional)
 */
const ConfirmationModal = ({
  open = false,
  title = 'Confirmação',
  message = 'Tem certeza que deseja continuar?',
  type = 'warning',
  onConfirm,
  onCancel,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
  description = null,
  confirmButtonColor = 'error'
}) => {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <WarningIcon sx={{ fontSize: 48, color: '#ff9800', mr: 2 }} />;
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mr: 2 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 48, color: '#f44336', mr: 2 }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ fontSize: 48, color: '#2196f3', mr: 2 }} />;
    }
  };

  const getAlertSeverity = () => {
    switch (type) {
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'info':
      default:
        return 'info';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          borderBottom: '1px solid #eee'
        }}
      >
        {getIcon()}
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity={getAlertSeverity()} sx={{ mb: 1 }}>
            {message}
          </Alert>

          {description && (
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          borderTop: '1px solid #eee'
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmButtonColor}
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Processando...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
